import React, { useMemo, useState, useEffect } from 'react';
import { When } from 'react-if';
import { useSelector } from 'react-redux';
import {
    Box,
    Button,
    Card,
    Container,
    Grid,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import DeleteForeverOutlined from '@mui/icons-material/RemoveModeratorOutlined';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';

import Loading from '../../loading';
import { RootState } from '../../../store';
import { PaginationFactory, PaginatorFactory } from '../../../factory';
import { DEVICE_COLUMNS, DEVICE_COLUMNS_WITH_ASSET_TAG, DEVICE_FILTER_INPUTS_WITH_ASSET_TAG, DEVICE_FILTER_INPUTS_UNASSIGNED, EMPLOYEE_ID_COLUMN, GROUP_COLUMN, TEAM_COLUMN, SHIPPED_COLUMNS, SHIPPED_TABLE_COLUMNS } from '../../../builders/devices-builders';
import { DeviceService } from '../../../services/device.service';
import { DeviceTypes } from '../../../types';
import { DeviceListFilters } from './devices-filters';
import AreYouSureModal from '../../are-you-sure-modal';
import NoData from '../../no-data';
import { DeviceDetailsDrawer } from '../../details-drawers/device-details-drawer';
import OptionsDrawer, { DrawerOption } from '../../options-drawer';
import { downloadCsv } from '../../../hooks/useCsvDownload';
import { CloudUpload, ErrorOutline, PersonRemove, RemoveFromQueueSharp } from '@mui/icons-material';
import { ImportDevicesModal } from '../../import-devices-modal';
import { useGenericQuery } from '../../../services/react-query/generic.query';
import { TagsService } from '../../../services/tags-service';
import { Team } from '../../../types/teams.types';
import { Group } from '../../../types/groups.types';
import { useEffectiveSettings, SETTING } from '../../../hooks/use-effective-settings';

const RETURN_STATUS_OPTIONS = [
    { value: 'not_returned', label: 'Not Returned' },
    { value: 'returned', label: 'Returned' },
    { value: 'in_transit', label: 'In Transit' },
];

const getReturnStatusLabel = (value: string | null | undefined): string => {
    return RETURN_STATUS_OPTIONS.find(o => o.value === value)?.label ?? 'Not Set';
};

export const DeviceList: React.FC = () => {
    const [selectedDevice, setSelectedDevice] = useState<DeviceTypes.Model>({} as DeviceTypes.Model);
    const [detailsModal, setDetailsModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [unassignModal, setUnassignModal] = useState(false);
    const [optionsDrawer, setOptionsDrawer] = useState<boolean>(false);
    const [assigned, setAssigned] = useState<string>('true');    
    const [importDevicesModal, setImportDevicesModal] = useState<boolean>(false);

    const [sort, setSort] = useState<PaginatorFactory<any>['sort']>({ createdAt: 'desc' });
    const [filters, setFilters] = useState<DeviceTypes.Filters>({} as DeviceTypes.Filters);

    const {
        pagination: { total, items },
        loading,
    } = useSelector((state: RootState) => state.device);

    const { getSetting } = useEffectiveSettings();
    const assetManagementEnabled = getSetting(SETTING.assetManagement, false);

    const hideLastPing = getSetting(SETTING.hideLastPing, false);

    // Group/Team/Tag scope filter data (assigned tab). Same sources as the
    // POM Users list so the two filter bars behave identically.
    const allTags = useSelector((state: RootState) => state.tags.items);
    useEffect(() => {
        TagsService.fetchAll(() => undefined);
    }, []);
    const { data: allTeams } = useGenericQuery<Team[]>(
        'fetchTeams',
        'teams/fetchTeams',
        0,
        0,
        false,
    );
    const { data: allGroups } = useGenericQuery<Group[]>(
        'fetchGroups',
        'groups/fetch-all-groups-teams-users',
        0,
        0,
        false,
    );
    // Live group/team names for each assigned device's holder, resolved
    // from the same team/group lists the filter bar uses. Users carry only
    // teamId; group is derived via Team.groupId. String() keys tolerate the
    // mixed string/ObjectId teamId typing in older user docs.
    const teamContextByTeamId = useMemo(() => {
        const groupNameById = new Map<string, string>();
        for (const g of allGroups || []) groupNameById.set(String(g.id || (g as any)._id || ''), g.name);
        const map = new Map<string, { teamName: string; groupName: string }>();
        for (const t of allTeams || []) {
            map.set(String(t.id || (t as any)._id || ''), {
                teamName: t.name ?? '',
                groupName: (t.groupId && groupNameById.get(String(t.groupId))) || '',
            });
        }
        return map;
    }, [allTeams, allGroups]);
    const holderContext = (device: DeviceTypes.Model | undefined) =>
        teamContextByTeamId.get(String(device?.user?.teamId ?? ''));

    const baseColumns = assetManagementEnabled ? DEVICE_COLUMNS_WITH_ASSET_TAG : DEVICE_COLUMNS;
    const columns = useMemo(() => {
        let cols = baseColumns;
        if (assigned === 'true') {
            cols = [cols[0], EMPLOYEE_ID_COLUMN, GROUP_COLUMN, TEAM_COLUMN, ...cols.slice(1)];
        } else {
            // Unassigned devices have no user (User Name is always N/A) and
            // no live battery reading — drop both columns on this tab.
            cols = cols.filter((col) => col.field !== 'name' && col.field !== 'batteryLevel');
        }
        if (hideLastPing) {
            cols = cols.filter((col) => col.field !== 'updatedAt');
        }
        if (assetManagementEnabled && assigned === 'false') {
            return [...SHIPPED_TABLE_COLUMNS, ...cols, { field: 'returnStatus', headerName: 'Return Status', width: 150 }];
        }
        return cols;
    }, [assetManagementEnabled, assigned, baseColumns, hideLastPing]);
    const filterInputs = useMemo(() => {
        if (!assetManagementEnabled) return undefined;
        return assigned === 'false' ? DEVICE_FILTER_INPUTS_UNASSIGNED : DEVICE_FILTER_INPUTS_WITH_ASSET_TAG;
    }, [assetManagementEnabled, assigned]);

    const [pagination, setPagination] = useState<PaginatorFactory<any>>({
        page: 0,
        perPage: 25,
    });

    const onFilter = async (newFilters: DeviceTypes.Filters) => {
        // Store filters and reset to page 1 when filters change
        setFilters(newFilters);
        setPagination(prev => ({ ...prev, page: 0 }));
        
        await DeviceService.fetchMany({
            ...pagination,
            page: 1, // Always start at page 1 when filtering
            perPage: pagination.perPage,
            filter: { ...newFilters },
            sort,
        }, (isValid, msg) => {}, false, assigned === 'true' ? true : false);
    };

    const onDelete = async () => {
        await DeviceService.destroy(selectedDevice?.id, (isValid, msg) => {
            setDeleteModal(false);
            setSelectedDevice({} as DeviceTypes.Model);

            if (!isValid) {
                toast(msg, {
                    autoClose: 1500,
                    type: 'error',
                });
                return;
            }

            toast('Device successfully deleted.', {
                autoClose: 1500,
                type: 'success',
            });
        });
    };

    const onUnassign = async () => {
        await DeviceService.unassign(selectedDevice?.id, async (isValid, msg) => {
            setUnassignModal(false);
            setSelectedDevice({} as DeviceTypes.Model);

            if (!isValid) {
                toast(msg, {
                    autoClose: 1500,
                    type: 'error',
                });
                return;
            }

            await DeviceService.fetchMany({
                ...pagination,
                page: pagination.page + 1,
                filter: { },
                sort,
            }, (isValid, msg) => {}, false, assigned === 'true' ? true : false);

            toast('Device successfully unassigned from user.', {
                autoClose: 1500,
                type: 'success',
            });
        });
    }

    const makeCsvDownload = async () => {
        // Export honors the applied filters (and always exports from page 1)
        // so the CSV matches what's on screen — same contract as the POM
        // Users and Alerts exports.
        const res: PaginationFactory<DeviceTypes.Model> = await DeviceService.fetchMany(
            { perPage: 100000, page: 1, filter: { ...filters }, sort },
            undefined,
            true,
            assigned === 'true' ? true : false,
        ) as PaginationFactory<DeviceTypes.Model>;

        const csvArray = res.items?.map((device) => ({
            // Unassigned devices have no user — mirror the table and omit
            // the user columns there; shipped columns lead instead.
            ...(assigned === 'true' ? {
                userName: `${device?.user?.firstName ?? ''} ${device?.user?.lastName ?? ''}`,
                employeeId: device?.user?.employeeId ?? '',
                // Same keys/order as the emailed devices report CSV
                // (reportCsv.service.ts buildDevices).
                groupName: holderContext(device)?.groupName ?? '',
                teamName: holderContext(device)?.teamName ?? '',
            } : {}),
            ...(assetManagementEnabled && assigned === 'false'
                ? Object.fromEntries(SHIPPED_COLUMNS.map((c) => [c.field, device?.[c.field] ?? '']))
                : {}),
            serialNumber: device?.serialNumber ?? '',
            ...(assetManagementEnabled ? { assetTag: device?.assetTag ?? '' } : {}),
            macId: device?.macId ?? '',
            ...(assigned === 'true' ? {
                batteryLevel: device?.batteryLevel != null && device.batteryLevel >= 0 && device.batteryLevel <= 100 ? `${Math.round(device.batteryLevel)}%` : 'N/A',
            } : {}),
            createdAt: device?.createdAt ? dayjs(device?.createdAt).format('MM/DD/YYYY hh:mm A') : 'N/A',
            ...(!hideLastPing && { lastUpdate: device?.updatedAt ? dayjs(device?.updatedAt).format('MM/DD/YYYY hh:mm A') : 'N/A' }),
            ...(assetManagementEnabled && assigned === 'false' ? {
                returnStatus: getReturnStatusLabel(device?.returnStatus),
                notes: device?.notes ?? '',
            } : {}),
        }));

        const filenameString = assigned === 'true' ? 'assigned' : 'unassigned'
        downloadCsv(csvArray, `${filenameString}-devices`);
    }

    // Track previous assigned value to detect tab changes
    const prevAssignedRef = React.useRef(assigned);
    
    // Fetch devices when pagination, sort, filters, or tab changes
    useEffect(() => {
        // If tab changed, reset page, filters, AND sort. Sort must reset
        // because user.employeeId only exists on the assigned pipeline (the
        // unassigned query never $lookups the user) — carrying it across
        // makes every row tie, so $skip/$limit pagination can repeat or
        // drop rows with no visible sort indicator to clear.
        if (prevAssignedRef.current !== assigned) {
            prevAssignedRef.current = assigned;
            setPagination(prev => ({ ...prev, page: 0 }));
            setFilters({} as DeviceTypes.Filters);
            setSort({ createdAt: 'desc' });
            // Fetch with reset values
            DeviceService.fetchMany({
                perPage: pagination.perPage,
                page: 1,
                filter: {},
                sort: { createdAt: 'desc' },
            }, (isValid, msg) => {}, false, assigned === 'true');
            return;
        }
        
        // Normal fetch with current values
        DeviceService.fetchMany({
            ...pagination,
            page: pagination.page + 1,
            filter: filters,
            sort,
        }, (isValid, msg) => {}, false, assigned === 'true');
    }, [pagination?.perPage, pagination?.page, sort, assigned, filters, importDevicesModal]);

    return (
        <>
            <Grid container display={'flex'} flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'} sx={{ mb: 2 }}>
                <Tabs value={assigned} onChange={(e, v) => setAssigned(v)}>
                    <Tab label="Assigned" value={'true'}/>
                    <Tab label="Unassigned" value={'false'} />
                </Tabs>
                <Button endIcon={<CloudUpload />} onClick={() => setImportDevicesModal(true)}>
                    Import Devices 
                </Button>
            </Grid>
            <Card sx={{ mt: 2 }}>
                <DeviceListFilters
                    // Remount on tab switch so child filter state resets in
                    // lockstep with the parent's filters reset in useEffect.
                    key={assigned}
                    onFilter={onFilter}
                    onDownload={async () => {
                        await makeCsvDownload();
                    }}
                    filterInputs={filterInputs}
                    showScopeFilters={assigned === 'true'}
                    showUserNameFilter={assigned === 'true'}
                    showLastUpdateFilter={!hideLastPing}
                    allTeams={allTeams || []}
                    allGroups={allGroups || []}
                    allTags={allTags || []}
                />
                <TableContainer>
                    <Table>
                        <TableHead sx={{ m: 0, p: 0 }}>
                            <TableRow>
                                {columns?.map((column) => (
                                    <TableCell
                                        key={column?.field}
                                        sortDirection={sort![column.field] || undefined}
                                    >
                                        {(column as { sortable?: boolean }).sortable === false ? (
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'text.primary', whiteSpace: 'nowrap' }}
                                            >
                                                {column?.headerName}
                                            </Typography>
                                        ) : (
                                            <TableSortLabel
                                                active={!!sort![column.field]}
                                                direction={sort![column.field]}
                                                onClick={() => {
                                                    setSort((s) => ({
                                                        [column.field]: s![column.field] === 'asc' ? 'desc' : 'asc',
                                                    }));
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: 'text.primary', whiteSpace: 'nowrap' }}
                                                >
                                                    {column?.headerName}
                                                </Typography>
                                            </TableSortLabel>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {!loading.fetchMany && (items || [])?.map((item) => {
                                return (
                                    <TableRow
                                        key={item?.id}
                                        onClick={() => {
                                            setSelectedDevice(item);
                                            setOptionsDrawer(true);
                                        }}
                                        sx={{ 
                                            ":hover": { 
                                                backgroundColor: '#F0F0F0',
                                                cursor: 'pointer'
                                            }}
                                        } 
                                    >
                                        {assigned === 'true' && (
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: 'text.secondary' }}
                                                >
                                                    {item?.user?.firstName ? `${item?.user?.firstName} ${item?.user?.lastName}` : 'N/A'}
                                                </Typography>
                                            </TableCell>
                                        )}

                                        {assigned === 'true' && (
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: 'text.secondary' }}
                                                >
                                                    {item?.user?.employeeId || 'N/A'}
                                                </Typography>
                                            </TableCell>
                                        )}

                                        {assigned === 'true' && (
                                            <>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ color: 'text.secondary' }}
                                                    >
                                                        {holderContext(item)?.groupName || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ color: 'text.secondary' }}
                                                    >
                                                        {holderContext(item)?.teamName || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                            </>
                                        )}

                                        {assetManagementEnabled && assigned === 'false' && (
                                            <>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ color: 'text.secondary' }}
                                                    >
                                                        {item?.shippedTo || 'N/A'}
                                                    </Typography>
                                                    {item?.shippedToEmployeeId && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{ color: 'text.disabled', display: 'block' }}
                                                        >
                                                            {item.shippedToEmployeeId}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ color: 'text.secondary' }}
                                                    >
                                                        {[item?.shippedToGroup, item?.shippedToTeam].filter(Boolean).join(' · ') || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                            </>
                                        )}

                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'text.secondary' }}
                                            >
                                                {item?.serialNumber}
                                            </Typography>
                                        </TableCell>

                                        {assetManagementEnabled && (
                                            <TableCell>
                                                {item?.assetTag ? (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ color: 'text.secondary' }}
                                                    >
                                                        {item.assetTag}
                                                    </Typography>
                                                ) : (
                                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                                        <ErrorOutline sx={{ color: 'warning.main', fontSize: 18 }} />
                                                        <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: 13 }}>
                                                            Not assigned
                                                        </Typography>
                                                    </Stack>
                                                )}
                                            </TableCell>
                                        )}

                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'text.secondary' }}
                                            >
                                                {item?.macId ?? 'N/A'}
                                            </Typography>
                                        </TableCell>

                                        {assigned === 'true' && (
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: 'text.secondary' }}
                                                >
                                                    {item?.batteryLevel != null && item.batteryLevel >= 0 && item.batteryLevel <= 100 ? `${Math.round(item.batteryLevel)} %` : 'N/A'}
                                                </Typography>
                                            </TableCell>
                                        )}

                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'text.secondary' }}
                                            >
                                                {item.createdAt ? dayjs(item.createdAt).format('MM/DD/YYYY hh:mm A') : '----'}
                                            </Typography>
                                        </TableCell>

                                        {!hideLastPing && (
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: 'text.secondary' }}
                                                >
                                                    {item.updatedAt ? dayjs(item.updatedAt).format('MM/DD/YYYY hh:mm A') : '----'}
                                                </Typography>
                                            </TableCell>
                                        )}

                                        {assetManagementEnabled && assigned === 'false' && (
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: 'text.secondary' }}
                                                >
                                                    {getReturnStatusLabel(item?.returnStatus)}
                                                </Typography>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>

                    <When condition={loading?.fetchMany}>
                        <Loading />
                    </When>

                    <When
                        condition={
                            loading.fetchMany === false && items?.length < 1
                        }
                    >
                        <NoData
                            buttonText={null}
                            onClick={() => {}}
                            title={'No records found'}
                            description={
                                'There are no devices registered.'
                            }
                        />
                    </When>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[25, 50, 100]}
                    component="div"
                    count={total ?? 0}
                    rowsPerPage={pagination?.perPage}
                    page={pagination?.page}
                    onPageChange={(e, p) =>
                        setPagination((pag) => ({ ...pag, page: p }))
                    }
                    onRowsPerPageChange={(e) =>
                        setPagination((pag) => ({
                            ...pag,
                            perPage: parseInt(e.target.value, 10),
                        }))
                    }
                />
            </Card>

            {importDevicesModal && 
                <ImportDevicesModal 
                    visible={importDevicesModal}
                    onClose={() => {
                        setImportDevicesModal(false);
                    }}
                />
            }
            <AreYouSureModal
                title={'Are you sure you want to delete this item?'}
                description={'You will permanently lose this record.'}
                onHide={() => {
                    setSelectedDevice({} as DeviceTypes.Model);
                    setDeleteModal(false);
                }}
                loading={loading?.delete}
                onConfirm={onDelete}
                visible={deleteModal}
            />

            <AreYouSureModal
                title={'Are you sure you want to unassign this device from the user?'}
                description={'The user will no longer have access to this device.'}
                onHide={() => {
                    setSelectedDevice({} as DeviceTypes.Model);
                    setUnassignModal(false);
                }}
                loading={loading?.unassign}
                onConfirm={onUnassign}
                visible={unassignModal}
            />

            <OptionsDrawer 
                anchor="bottom"
                onClose={() => {
                    setOptionsDrawer(false);
                }}
                open={optionsDrawer}
                options={[
                    {
                        label: 'See Device Details',
                        icon: <ManageSearchIcon />,
                        onClick: () => {
                            setOptionsDrawer(false);
                            setDetailsModal(true);
                        },
                    },
                    selectedDevice?.userId && {
                        label: 'Unassign Device from User',
                        icon: <PersonRemove />,
                        onClick: () => {
                            setOptionsDrawer(false);
                            setUnassignModal(true);
                        },
                    },
                    {
                        label: 'Remove Device From Subscription',
                        icon: <DeleteForeverOutlined />,
                        onClick: () => {
                            setOptionsDrawer(false);
                            setDeleteModal(true);
                        },
                    },
                ].filter(Boolean) as DrawerOption[]}
            />

            <When condition={detailsModal}>
                <DeviceDetailsDrawer
                    device={selectedDevice}
                    visible={detailsModal}
                    assetManagementEnabled={assetManagementEnabled}
                    onClose={() => {
                        setDetailsModal(false);
                        setSelectedDevice({} as DeviceTypes.Model);
                    }}
                    onUpdate={() => {
                        DeviceService.fetchMany({
                            ...pagination,
                            page: pagination.page + 1,
                            filter: filters,
                            sort,
                        }, () => {}, false, assigned === 'true');
                    }}
                />
            </When>
        </>
    );
};
